/**
 * Custom React Flow node components for workflow services.
 * Each renders with service-specific styling, handles (connectors), and labels.
 */
import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';

// ============================================
// Shared Node Shell
// ============================================
const NodeShell = ({ data, color, icon, selected }) => (
    <div
        style={{
            minWidth: '180px',
            background: '#1E293B',
            borderRadius: '12px',
            border: `2px solid ${selected ? color : `${color}66`}`,
            boxShadow: selected ? `0 0 0 2px ${color}33, 0 8px 24px rgba(0,0,0,0.3)` : '0 4px 12px rgba(0,0,0,0.2)',
            transition: 'all 0.2s ease',
            overflow: 'hidden',
        }}
    >
        {/* Header */}
        <div style={{ background: `${color}1A`, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: `1px solid ${color}33` }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                {icon}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ color: '#F1F5F9', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {data.name || data.label}
                </div>
                <div style={{ color: '#94A3B8', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {data.serviceType}
                </div>
            </div>
        </div>

        {/* Body — show config hints */}
        {data.config && Object.keys(data.config).length > 0 && (
            <div style={{ padding: '8px 14px' }}>
                {Object.entries(data.config).slice(0, 2).map(([key, val]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94A3B8', marginBottom: '2px' }}>
                        <span>{key}:</span>
                        <span style={{ color: '#CBD5E1', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(val)}</span>
                    </div>
                ))}
            </div>
        )}

        {/* Handles */}
        <Handle type="target" position={Position.Top} style={{ background: color, width: 10, height: 10, border: '2px solid #1E293B' }} />
        <Handle type="source" position={Position.Bottom} style={{ background: color, width: 10, height: 10, border: '2px solid #1E293B' }} />
    </div>
);


// ============================================
// DoqSolv Node
// ============================================
const doqsolvIcon = (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
);

const DoqSolvNode = memo(({ data, selected }) => (
    <NodeShell data={data} color="#6366F1" icon={doqsolvIcon} selected={selected} />
));
DoqSolvNode.displayName = 'DoqSolvNode';


// ============================================
// Live Stamping Node
// ============================================
const liveStampingIcon = (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const LiveStampingNode = memo(({ data, selected }) => (
    <NodeShell data={data} color="#10B981" icon={liveStampingIcon} selected={selected} />
));
LiveStampingNode.displayName = 'LiveStampingNode';


// ============================================
// eSign Node
// ============================================
const esignIcon = (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
);

const ESignNode = memo(({ data, selected }) => (
    <NodeShell data={data} color="#3B82F6" icon={esignIcon} selected={selected} />
));
ESignNode.displayName = 'ESignNode';


// ============================================
// Mock Node (for testing)
// ============================================
const mockIcon = (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
);

const MockNode = memo(({ data, selected }) => (
    <NodeShell data={data} color="#6B7280" icon={mockIcon} selected={selected} />
));
MockNode.displayName = 'MockNode';


// ============================================
// Node Types Registry (pass to ReactFlow)
// ============================================
const nodeTypes = {
    doqsolv: DoqSolvNode,
    live_stamping: LiveStampingNode,
    esign: ESignNode,
    mock: MockNode,
};

export { DoqSolvNode, LiveStampingNode, ESignNode, MockNode };
export default nodeTypes;
